/**
 * ConnectionController — manages call lifecycle (connect, disconnect, mic, audio session).
 *
 * Lit Reactive Controller equivalent of useRinggCall from React Native.
 */

import type { ReactiveController, ReactiveControllerHost } from "lit";

import type { RealtimeRoomAdapter, ConnectionState, MediaType, SlashCommand } from "@ringg/core";
import { RinggApiClient, WidgetStateController, isAgentIdentity } from "@ringg/core";

export class ConnectionController implements ReactiveController {
  host: ReactiveControllerHost;

  private adapter: RealtimeRoomAdapter;
  private api: RinggApiClient;
  private widgetState: WidgetStateController;
  private roomServerUrl: string;
  private agentId: string;
  private variables: Record<string, string>;
  private cleanupFns: Array<() => void> = [];

  connectionState: ConnectionState = "disconnected";
  isLoading = false;
  callMode: MediaType = "audio";
  callId: string | null = null;
  isMicEnabled = false;
  error: string | null = null;
  runtimeSlashCommands: SlashCommand[] = [];
  private _endCallHandled = false;

  constructor(
    host: ReactiveControllerHost,
    opts: {
      adapter: RealtimeRoomAdapter;
      api: RinggApiClient;
      widgetState: WidgetStateController;
      roomServerUrl: string;
      agentId: string;
      variables: Record<string, string>;
      defaultTab?: MediaType;
    },
  ) {
    this.host = host;
    this.adapter = opts.adapter;
    this.api = opts.api;
    this.widgetState = opts.widgetState;
    this.roomServerUrl = opts.roomServerUrl;
    this.agentId = opts.agentId;
    this.variables = opts.variables;
    this.callMode = opts.defaultTab ?? "audio";
    host.addController(this);
  }

  hostConnected(): void {
    // Track connection state changes
    const unsubConn = this.adapter.onConnectionStateChange((state) => {
      this.connectionState = state;
      if (state === "disconnected") {
        this.isMicEnabled = false;
      }
      this.host.requestUpdate();
    });

    // Auto-end on agent disconnect
    const unsubParticipant = this.adapter.onParticipantDisconnected((identity) => {
      if (isAgentIdentity(identity)) {
        this._handleEndCall();
      }
    });

    // Auto-end on room disconnect
    const unsubRoom = this.adapter.onDisconnected(() => {
      this._handleEndCall();
    });

    this.cleanupFns.push(unsubConn, unsubParticipant, unsubRoom);
  }

  hostDisconnected(): void {
    this.cleanupFns.forEach((fn) => fn());
    this.cleanupFns = [];
  }

  get isConnected(): boolean {
    return this.connectionState === "connected";
  }

  async startCall(mediaType: MediaType): Promise<void> {
    this.isLoading = true;
    this.error = null;
    this.callMode = mediaType;
    this.widgetState.setCallMode(mediaType);
    this.host.requestUpdate();

    try {
      const response = await this.api.startWebcall({
        agentId: this.agentId,
        variables: this.variables,
        mediaType,
      });

      this.callId = response.call_id;
      this.widgetState.setCurrentCallId(response.call_id);

      if (Array.isArray(response.enabled_slash_commands)) {
        this.runtimeSlashCommands = response.enabled_slash_commands;
      }

      // Request mic permission for audio mode
      if (mediaType === "audio") {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          stream.getTracks().forEach((t) => t.stop());
        } catch {
          this.error = "Microphone permission denied. Please allow mic access.";
          this.isLoading = false;
          this.host.requestUpdate();
          return;
        }
      }

      // Audio session (no-op on web, but call for interface consistency)
      await this.adapter.configureAudio();
      await this.adapter.startAudioSession();

      // Connect
      await this.adapter.connect(this.roomServerUrl, response.user_token);

      // Enable mic for audio mode
      if (mediaType === "audio") {
        try {
          await this.adapter.setMicrophoneEnabled(true);
          this.isMicEnabled = true;
        } catch {
          this.error = "Microphone access failed. Check permissions.";
        }
      }

      this._endCallHandled = false;
      this.widgetState.dispatchConversationStart(mediaType, response.call_id);
    } catch (err) {
      this.adapter.stopAudioSession().catch(() => {});
      this.error = (err as Error).message;
    } finally {
      this.isLoading = false;
      this.host.requestUpdate();
    }
  }

  async endCall(): Promise<void> {
    this.isLoading = true;
    this.host.requestUpdate();
    try {
      this._handleEndCall();
    } finally {
      this.isLoading = false;
      this.host.requestUpdate();
    }
  }

  async toggleMic(): Promise<void> {
    try {
      const newState = !this.adapter.isMicrophoneEnabled();
      await this.adapter.setMicrophoneEnabled(newState);
      this.isMicEnabled = newState;
      this.host.requestUpdate();
    } catch {
      // Toggle failure — mic state may be stale
    }
  }

  clearError(): void {
    this.error = null;
    this.host.requestUpdate();
  }

  private _handleEndCall(): void {
    // Guard against double invocation (agent disconnect + room disconnect can race)
    if (this._endCallHandled) return;
    this._endCallHandled = true;

    if (this.callId) {
      this.widgetState.dispatchConversationEnd(this.callMode, this.callId);
    }

    this.adapter.disconnect();
    this.adapter.stopAudioSession().catch(() => {});

    this.runtimeSlashCommands = [];
    this.isMicEnabled = false;
    this.connectionState = "disconnected";
    this.widgetState.showFeedback();
    this.host.requestUpdate();
  }
}
